import { AfterViewInit, Component, ElementRef, Input, ViewChild, ViewEncapsulation } from '@angular/core';
import { Attachment, NonceUtility } from '@jms/model';
import { azure } from 'fine-uploader/lib/azure';
import { DragAndDrop } from 'fine-uploader/lib/dnd';
import { FileInfo, UploaderActions } from './uploader.actions';
import { UploaderService } from './uploader.service';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';

@Component({
  selector: 'uploader-centre',
  templateUrl: 'uploader.centre.component.html',
  styleUrls: ['uploader.centre.component.less'],
  encapsulation: ViewEncapsulation.None,
  standalone: false
})
export class UploaderCentreComponent implements AfterViewInit {
  public isUploading: boolean;
  public fileCount = 1;
  public files: FileInfo[];
  public isShowDialog: boolean;

  public uploader: azure.FineUploader;
  public dragAndDrop: DragAndDrop;

  @Input() public tenantId: string;
  @Input() public signingEndpoint: string;
  @Input() public successEndpoint: string;
  @Input() public endpoint: string;
  @Input() public token: string;

  @ViewChild('uploadFilesButton', { static: true }) uploadFilesButton: ElementRef;
  @ViewChild('uploadImagesButton', { static: true }) uploadImagesButton: ElementRef;
  @ViewChild('uploadAllTypesButton', { static: true }) uploadAllTypesButton: ElementRef;

  constructor(public actions: UploaderActions, private uploadService: UploaderService) { }

  public showDetails(isVisible: boolean) {
    this.actions.showUploadDetails(isVisible);
  }

  ngAfterViewInit(): void {
    const options = {
      // button: this.uploadFilesButton.nativeElement,
      autoUpload: true,
      debug: false,
      extraButtons: [
        {
          element: this.uploadAllTypesButton.nativeElement,
          multiple: true,
          validation: {
            acceptFiles:
              'application/vnd.ms-excel.sheet.macroEnabled.12,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/*,text/plain',
            allowedExtensions: ['doc', 'docx', 'pdf', 'xlsm', 'xlsx', 'xls', 'csv', 'txt', 'jpg', 'jpeg', 'jpe', 'jfif', 'png', 'PNG', 'webp']
          }
        },
        {
          element: this.uploadImagesButton.nativeElement,
          multiple: true,
          validation: {
            acceptFiles: 'image/*'
          }
        },
        {
          element: this.uploadFilesButton.nativeElement,
          multiple: true,
          validation: {
            acceptFiles:
              'application/vnd.ms-excel.sheet.macroEnabled.12,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain',
            allowedExtensions: ['doc', 'docx', 'pdf', 'xlsm', 'xlsx', 'xls', 'csv', 'txt']
          }
        }
      ],
      request: {
        containerUrl: this.endpoint,
        endpoint: this.endpoint,
        // customHeaders: {
        //     Authorization: this.token
        // },
        params: {
          tenantid: this.tenantId,
          entityid: ''
        }
      },
      signature: {
        customHeaders: () => this.getHeaders(),
        endpoint: this.signingEndpoint
      },
      uploadSuccess: {
        customHeaders: () => this.getHeaders(),
        endpoint: this.successEndpoint
      },
      cors: {
        allowXdr: true,
        expected: true
      },
      chunking: {
        enabled: true,
        concurrent: {
          enabled: true
        },
        partSize: 2000000,
        minFileSize: 2000001
      },
      scaling: {
        sendOriginal: false,
        // sizes: [{ name: '', maxSize: 1920 }],
        includeExif: true
      },
      callbacks: {
        onSubmit: (id: number, name: string, attachment: Attachment) => {
          if (this.uploader.metadata) {
            this.actions.addFile(id, name, this.uploader.metadata);
          } else {
            this.actions.addFile(id, name);
          }
        },
        onUpload: (id: number, name: string) => {
          console.log('onUpload callback ' + id + ' ' + name);
          this.actions.beginUpload(id, name);
        },
        onUploadChunk: (id: number, name: string, chunkData: any) => {
          console.log('onUploadChunk callback ' + id + ' ' + name);
        },
        onComplete: (id, name, res, xhr) => {
          this.actions.uploadCompleted(id, name, res.attachment._id ? 'success' : 'failed', res);
        },
        onProgress: (id: number, name: string, uploadedBytes: number, totalBytes: number) => {
          this.actions.onProgress(id, name, uploadedBytes, totalBytes);
        },
        onStatusChange: (id: number, oldStatus, newStatus) => { },
        onAllComplete: (succeeded, failed) => {
          this.actions.uploadAllCompleted();
        },
        onTotalProgress: (totalUploadedBytes: number, totalBytes: number) => {
          const progress = totalBytes > 0 ? Math.round(totalUploadedBytes / totalBytes * 100) : 0;

          this.actions.onTotalProgress(progress);
        },
        onError: (id: number, name: string, reason: string) => {
          this.actions.uploadCompleted(id, name, 'failed', null);
        }
      }
    };

    this.dragAndDrop = new DragAndDrop({
      classes: {
        dropActive: 'dropActive'
      },
      callbacks: {
        processingDroppedFilesComplete: (files, dropTarget) => {
          this.uploadService.dropFiles(files, dropTarget);
        }
      }
    });

    this.uploader = new azure.FineUploaderBasic(options);
    this.uploadService.UploadFilesButton = this.uploadFilesButton;
    this.uploadService.UploadImageButton = this.uploadImagesButton;
    this.uploadService.UploadAllTypesButton = this.uploadAllTypesButton;
    this.uploadService.Uploader = this.uploader;
    this.uploadService.DragAndDrop = this.dragAndDrop;
    this.actions.initServiceAlready();
  }

  private getNonce() {
    const environmentNonce = ConfigService.environment.nonce;
    const tenant = ConfigService.environment.tenant;
    const hostname = window.location.hostname;
    const nonce = NonceUtility.tryGenerate(tenant, hostname, environmentNonce);
    return nonce;
  }

  private getHeaders() {

    const headers = {
      Authorization: this.token
    };

    const nonce = this.getNonce();

    if (nonce) {
      headers['Nonce'] = nonce;
    }

    return headers;

  }
}
